import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaSearch, FaFilter, FaSort, FaEye, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import axios from 'axios';
import Pagination from './Pagination'; // Import the new Pagination component
import ConfirmationDialog from './ConfirmationDialog'; // Make sure to import this

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 5000,
});

const AllCouponsList = () => {
  const { restaurantId } = useParams();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    currentPage: 1,
    couponsPerPage: 10,
    searchTerm: '',
    statusFilter: 'all',
    // dateFilter: { start: null, end: null },
    // sortField: 'createdAt',
    // sortDirection: 'desc',
    messageTypeFilter: 'all',
    dateFilter: { start: null, end: null },
    sortField: 'createdAt',
    sortDirection: 'desc',
    expirationFilter: 'all', // Add this new filter
  });
  const [selectedCoupons, setSelectedCoupons] = useState([]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  const fetchCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ownerToken');

      const response = await api.get(
        `/restaurants/${restaurantId}/coupons/all`,
        {
          //   params: {
          //     page: filters.currentPage,
          //     limit: filters.couponsPerPage,
          //     sortBy: filters.sortField,
          //     sortOrder: filters.sortDirection,
          //     status: filters.statusFilter,
          //     search: filters.searchTerm,
          //     startDate: filters.dateFilter.start,
          //     endDate: filters.dateFilter.end,
          //   },
          params: {
            page: filters.currentPage,
            limit: filters.couponsPerPage,
            sortBy: filters.sortField,
            sortOrder: filters.sortDirection,
            status:
              filters.statusFilter !== 'all' ? filters.statusFilter : undefined,
            messageType:
              filters.messageTypeFilter !== 'all'
                ? filters.messageTypeFilter
                : undefined,
            search: filters.searchTerm,
            startDate: filters.dateFilter.start,
            endDate: filters.dateFilter.end,
            expiration:
              filters.expirationFilter !== 'all'
                ? filters.expirationFilter
                : undefined, // Add this new parameter
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCoupons(response.data.coupons);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCoupons: response.data.totalCoupons,
        redeemedCount: response.data.redeemedCount,
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch coupons. Please try again.');
      toast.error('Failed to fetch coupons. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, filters]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleSearchChange = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
  }, 300);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
      currentPage: filterName === 'currentPage' ? value : 1,
    }));
  };

  const resetFilters = () => {
    setFilters({
      //   currentPage: 1,
      //   couponsPerPage: 10,
      //   searchTerm: '',
      //   statusFilter: 'all',
      //   dateFilter: { start: null, end: null },
      //   sortField: 'createdAt',
      //   sortDirection: 'desc',
      currentPage: 1,
      couponsPerPage: 10,
      searchTerm: '',
      statusFilter: 'all',
      messageTypeFilter: 'all',
      dateFilter: { start: null, end: null },
      sortField: 'createdAt',
      sortDirection: 'desc',
      expirationFilter: 'all', // Add this new filter
    });
  };

  // const handleDeleteCoupon = async (couponId) => {
  //   try {
  //     const token = localStorage.getItem('ownerToken');
  //     await api.delete(`/restaurants/${restaurantId}/coupons/${couponId}`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     toast.success('Coupon deleted successfully');
  //     fetchCoupons();
  //   } catch (error) {
  //     toast.error('Failed to delete coupon. Please try again.');
  //   }
  // };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleSelectCoupon = (couponId) => {
    setSelectedCoupons((prev) =>
      prev.includes(couponId)
        ? prev.filter((id) => id !== couponId)
        : [...prev, couponId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedCoupons(memoizedCoupons.map((coupon) => coupon._id));
    } else {
      setSelectedCoupons([]);
    }
  };

  const deleteSelectedCoupons = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      await api.post(
        `/restaurants/${restaurantId}/delete-coupons`,
        { couponIds: selectedCoupons },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Selected coupons deleted successfully');
      fetchCoupons();
      setSelectedCoupons([]);
    } catch (error) {
      toast.error('Failed to delete selected coupons');
    }
  };

  const deleteSingleCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('ownerToken');
      await api.delete(
        `/restaurants/${restaurantId}/coupons/single/${couponId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleDeleteClick = (couponId) => {
    setDeleteAction({ type: 'single', id: couponId });
    setShowConfirmDialog(true);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedCoupons.length > 0) {
      setDeleteAction({ type: 'multiple', ids: selectedCoupons });
      setShowConfirmDialog(true);
    } else {
      toast.warn('No coupons selected');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      if (deleteAction.type === 'single') {
        await api.delete(
          // `/restaurants/${restaurantId}/coupons/${deleteAction.id}`,
          `/restaurants/${restaurantId}/coupons/single/${deleteAction.id}`,

          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Coupon deleted successfully');
      } else if (deleteAction.type === 'multiple') {
        await api.post(
          // `/restaurants/${restaurantId}/coupons/delete-multiple`,
          `/restaurants/${restaurantId}/delete-coupons`,

          {
            couponIds: deleteAction.ids,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Selected coupons deleted successfully');
      }
      fetchCoupons();
      setSelectedCoupons([]);
    } catch (error) {
      toast.error('Failed to delete coupon(s)');
    } finally {
      setShowConfirmDialog(false);
      setDeleteAction(null);
    }
  };

  const memoizedCoupons = useMemo(() => coupons, [coupons]);

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-3xl font-bold'>All Coupons</h1>
        {/* <Link
          to={`/restaurant/${restaurantId}/dashboard`}
          className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md'
        >
          Back to Dashboard
        </Link> */}
      </div>

      <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search coupon code or phone number...'
          className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-4'>
        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
            value={filters.statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='redeemed'>Redeemed</option>
            <option value='active'>Not Redeemed</option>
          </select>
        </div>

        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('expirationFilter', e.target.value)
            }
            value={filters.expirationFilter}
          >
            <option value='all'>All Expiration</option>
            <option value='expired'>Expired</option>
            <option value='not_expired'>Not Expired</option>
            <option value='no_expiration'>No Expiration Date</option>
          </select>
        </div>

        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('messageTypeFilter', e.target.value)
            }
            value={filters.messageTypeFilter}
          >
            <option value='all'>All Message Types</option>
            <option value='text'>Text Message</option>
            <option value='welcome'>Welcome Message</option>
            <option value='reward'>Stamp Reward</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Created (From):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                start: e.target.value,
              })
            }
            value={filters.dateFilter.start || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Created (To):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                end: e.target.value,
              })
            }
            value={filters.dateFilter.end || ''}
          />
        </div>

        <div className='flex items-center'>
          <FaSort className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('sortField', e.target.value)}
            value={filters.sortField}
          >
            <option value='createdAt'>Sort by Creation Date</option>
            <option value='code'>Sort by Code</option>
            <option value='status'>Sort by Status</option>
            <option value='expirationDate'>Sort by Expiration Date</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              handleFilterChange(
                'sortDirection',
                filters.sortDirection === 'asc' ? 'desc' : 'asc'
              )
            }
          >
            {filters.sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div>

      <div className='mb-4'>
        <p>Total Coupons: {pagination.totalCoupons}</p>
        <p>
          Redeemed: {pagination.redeemedCount} out of {pagination.totalCoupons}
        </p>
      </div>
      {/* 
      <div className='mb-4 flex justify-between items-center'>
        <div>
          <label className='inline-flex items-center'>
            <input
              type='checkbox'
              checked={selectedCoupons.length === memoizedCoupons.length}
              onChange={handleSelectAll}
              className='mr-2'
            />
            <span>Select All</span>
          </label>
        </div>
        <div>
          <span className='mr-4'>
            Selected: {selectedCoupons.length} out of {memoizedCoupons.length}
          </span>
          <button
            onClick={deleteSelectedCoupons}
            disabled={selectedCoupons.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button>
        </div>
      </div> */}

      <div className='mb-4 flex justify-between items-center'>
        <div>
          <label className='inline-flex items-center'>
            <input
              type='checkbox'
              checked={selectedCoupons.length === memoizedCoupons.length}
              onChange={handleSelectAll}
              className='mr-2'
            />
            <span>Select All</span>
          </label>
        </div>
        <div>
          <span className='mr-4'>
            Selected: {selectedCoupons.length} out of {memoizedCoupons.length}
          </span>
          <button
            onClick={handleDeleteSelectedClick}
            disabled={selectedCoupons.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button>
        </div>
      </div>

      {error ? (
        <div className='text-red-500 text-xl text-center'>{error}</div>
      ) : loading ? (
        <div className='flex justify-center items-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='mt-6'>
          {memoizedCoupons.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {memoizedCoupons.map((coupon) => (
                <CouponCard
                  key={coupon._id}
                  coupon={coupon}
                  // onDelete={() => deleteSingleCoupon(coupon._id)}
                  onDelete={() => handleDeleteClick(coupon._id)} // Changed this line
                  isSelected={selectedCoupons.includes(coupon._id)}
                  onSelect={() => handleSelectCoupon(coupon._id)}
                />
              ))}
            </div>
          ) : (
            <p className='text-center'>No coupons found.</p>
          )}
        </div>
      )}

      {!loading && !error && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={filters.couponsPerPage}
          totalItems={pagination.totalCoupons}
        />
      )}

      <div className='flex justify-between items-center mt-4 text-sm text-gray-600'>
        {/* <div className='flex flex-col sm:flex-row justify-between items-center mt-6 text-sm text-gray-600'> */}

        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.couponsPerPage}
            onChange={(e) =>
              handleFilterChange('couponsPerPage', Number(e.target.value))
            }
            // className='p-2 border rounded'
            className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          >
            {/* <option value='1'>1</option> */}
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.couponsPerPage + 1}{' '}
            -{' '}
            {Math.min(
              pagination.currentPage * filters.couponsPerPage,
              pagination.totalCoupons
            )}{' '}
            of {pagination.totalCoupons}
          </span>
        </div>
      </div>
      {/* <div className='flex justify-center mt-5'>
        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
          (number) => (
            <button
              key={number}
              onClick={() => handleFilterChange('currentPage', number)}
              className={`mx-1 px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          )
        )}
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.couponsPerPage}
            onChange={(e) =>
              handleFilterChange('couponsPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.couponsPerPage + 1}{' '}
            -{' '}
            {Math.min(
              pagination.currentPage * filters.couponsPerPage,
              pagination.totalCoupons
            )}{' '}
            of {pagination.totalCoupons}
          </span>
        </div>
      </div> */}

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title='Confirm Deletion'
        message={
          deleteAction?.type === 'single'
            ? 'Are you sure you want to delete this coupon? This action cannot be undone.'
            : `Are you sure you want to delete ${deleteAction?.ids.length} selected coupons? This action cannot be undone.`
        }
      />
    </div>
  );
};
const CouponCard = React.memo(
  // ({ coupon, onDelete, isSelected, onSelect }) => (
  ({ coupon, messageContent, isSelected, onSelect, onDelete }) => (
    // <div
    //   className={`bg-white shadow-lg p-4 rounded relative ${
    //     isSelected ? 'border-2 border-blue-500' : ''
    //   } cursor-pointer hover:shadow-xl transition-shadow duration-300`}
    //   onClick={(e) => {
    //     if (e.target.type !== 'checkbox' && e.target.tagName !== 'BUTTON') {
    //       onSelect();
    //     }
    //   }}
    // >
    <div
      className={`bg-white shadow-lg p-4 rounded relative ${
        isSelected ? 'border-2 border-blue-500' : ''
      } cursor-pointer hover:shadow-xl transition-shadow duration-300`}
      onClick={(e) => {
        if (
          e.target.type !== 'checkbox' &&
          e.target.tagName !== 'BUTTON' &&
          e.target.tagName !== 'A'
        ) {
          onSelect();
        }
      }}
    >
      <input
        type='checkbox'
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className='absolute top-2 right-2'
      />
      <h2 className='text-lg font-semibold'>Coupon Code: {coupon.code}</h2>
      <p className='text-sm text-gray-500'>
        Message Type: {coupon.messageType}
        {coupon.isMessageDeleted && (
          <span className='ml-2 bg-red-500 text-white px-2 py-1 rounded text-xs'>
            Original Message Deleted
          </span>
        )}
      </p>
      <p className='text-sm text-gray-500'>
        Message Content: {coupon.messageContent}
      </p>
      <p className='text-sm text-gray-500'>
        Subscriber Phone: {coupon.customer?.phoneNumber || 'N/A'}
      </p>
      <p className='text-sm text-gray-500'>
        Status: {coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
      </p>
      <p className='text-sm text-gray-500'>
        Created: {new Date(coupon.createdAt).toLocaleString()}
      </p>
      <p className='text-sm text-gray-500'>
        Redeemed Date:{' '}
        {coupon.redeemedAt
          ? new Date(coupon.redeemedAt).toLocaleString()
          : 'N/A'}
      </p>
      <p className='text-sm text-gray-500'>
        Expires:{' '}
        {coupon.expirationDate
          ? new Date(coupon.expirationDate).toLocaleString()
          : 'N/A'}
      </p>
      {/* <div className='mt-4 flex flex-wrap gap-2'>
      <a
        href={`http://localhost:3000/redeem/${coupon.code}`}
        target='_blank'
        rel='noopener noreferrer'
        className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
      > */}
      <div className='mt-4 flex flex-wrap gap-2'>
        <a
          href={`http://localhost:3000/redeem/${coupon.code}`}
          target='_blank'
          rel='noopener noreferrer'
          className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
          onClick={(e) => e.stopPropagation()}
        >
          {/* <FaEye className='inline-block mr-2 text-center' /> */}
          <FaEye className='text-lg text-center' />
          {/* View Coupon */}
        </a>
        {/* <button
        onClick={() => onDelete(coupon._id)}
        className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
      > */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(coupon._id);
          }}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrash className='text-center ' />

          {/* <FaTrash className='inline-block mr-2' /> */}
          {/* Delete Coupon */}
        </button>
      </div>
    </div>
  )
);

// const CouponCard = React.memo(({ coupon, onDelete }) => (
//   <div className='bg-white shadow-lg p-4 rounded relative'>
//     <h2 className='text-lg font-semibold'>Coupon Code: {coupon.code}</h2>
//     <p className='text-sm text-gray-500'>
//       Message Type:{' '}
//       {coupon.textMessage
//         ? 'Text Message'
//         : coupon.welcomeTextMessage
//         ? 'Welcome Message'
//         : 'N/A'}
//       {(coupon.textMessage?.isDeleted ||
//         coupon.welcomeTextMessage?.isDeleted) && (
//         <span className='ml-2 bg-red-500 text-white px-2 py-1 rounded text-xs'>
//           Deleted
//         </span>
//       )}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Message Content:{' '}
//       {coupon.textMessage?.content ||
//         coupon.welcomeTextMessage?.content ||
//         'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Subscriber Phone: {coupon.customer?.phoneNumber || 'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Status: {coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Created: {new Date(coupon.createdAt).toLocaleString()}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Redeemed Date:{' '}
//       {coupon.redeemedAt ? new Date(coupon.redeemedAt).toLocaleString() : 'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Expires:{' '}
//       {coupon.expirationDate
//         ? new Date(coupon.expirationDate).toLocaleString()
//         : 'N/A'}
//     </p>
//     <div className='mt-4 flex flex-wrap gap-2'>
//       {/* <a
//         href={`${process.env.REACT_APP_FRONTEND_URL}/redeem/${coupon.code}`}
//         target='_blank'
//         rel='noopener noreferrer'
//         className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaEye className='inline-block mr-2' />
//         View Redeem Page
//       </a> */}
//       <a
//         href={`https://localhost:3000/redeem/${coupon.code}`}
//         target='_blank'
//         rel='noopener noreferrer'
//         className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaEye className='inline-block mr-2' />
//         View Redeem Page
//       </a>
//       <button
//         onClick={() => onDelete(coupon._id)}
//         className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaTrash className='inline-block mr-2' />
//         Delete Coupon
//       </button>
//     </div>
//   </div>
// ));

export default AllCouponsList;
// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import { FaSearch, FaFilter, FaSort, FaEye } from 'react-icons/fa';
// import { toast } from 'react-toastify';
// import { debounce } from 'lodash';
// import axios from 'axios';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
//   timeout: 5000,
// });

// const AllCouponsList = () => {
//   const { restaurantId } = useParams();
//   const [coupons, setCoupons] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [pagination, setPagination] = useState({});
//   const [filters, setFilters] = useState({
//     currentPage: 1,
//     couponsPerPage: 10,
//     searchTerm: '',
//     statusFilter: 'all',
//     dateFilter: { start: null, end: null },
//     sortField: 'createdAt',
//     sortDirection: 'desc',
//   });

//   const fetchCoupons = useCallback(async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem('ownerToken');

//       const response = await api.get(
//         `/restaurants/${restaurantId}/coupons/all`,
//         {
//           params: {
//             page: filters.currentPage,
//             limit: filters.couponsPerPage,
//             sortBy: filters.sortField,
//             sortOrder: filters.sortDirection,
//             status: filters.statusFilter,
//             search: filters.searchTerm,
//             startDate: filters.dateFilter.start,
//             endDate: filters.dateFilter.end,
//           },
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       setCoupons(response.data.coupons);
//       setPagination({
//         currentPage: response.data.currentPage,
//         totalPages: response.data.totalPages,
//         totalCoupons: response.data.totalCoupons,
//         redeemedCount: response.data.redeemedCount,
//       });
//       setError(null);
//     } catch (err) {
//       setError('Failed to fetch coupons. Please try again.');
//       toast.error('Failed to fetch coupons. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   }, [restaurantId, filters]);

//   useEffect(() => {
//     fetchCoupons();
//   }, [fetchCoupons]);

//   const handleSearchChange = debounce((value) => {
//     setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
//   }, 300);

//   const handleFilterChange = (filterName, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [filterName]: value,
//       currentPage: filterName === 'currentPage' ? value : 1,
//     }));
//   };

//   const resetFilters = () => {
//     setFilters({
//       currentPage: 1,
//       couponsPerPage: 10,
//       searchTerm: '',
//       statusFilter: 'all',
//       dateFilter: { start: null, end: null },
//       sortField: 'createdAt',
//       sortDirection: 'desc',
//     });
//   };

//   const memoizedCoupons = useMemo(() => coupons, [coupons]);

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <div className='flex justify-between items-center mb-6'>
//         <h1 className='text-3xl font-bold'>All Coupons</h1>
//         <Link
//           to={`/restaurant/${restaurantId}/dashboard`}
//           className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md'
//         >
//           Back to Dashboard
//         </Link>
//       </div>

//       {/* Search, filter, and sort controls (same as TextMessageCouponPage) */}
//       {/* ... */}

//       <div className='mb-4'>
//         <p>Total Coupons: {pagination.totalCoupons}</p>
//         <p>
//           Redeemed: {pagination.redeemedCount} out of {pagination.totalCoupons}
//         </p>
//       </div>

//       {error ? (
//         <div className='text-red-500 text-xl text-center'>{error}</div>
//       ) : loading ? (
//         <div className='flex justify-center items-center'>
//           <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
//         </div>
//       ) : (
//         <div className='mt-6'>
//           {memoizedCoupons.length > 0 ? (
//             <div className='grid grid-cols-1 gap-4'>
//               {memoizedCoupons.map((coupon) => (
//                 <CouponCard key={coupon._id} coupon={coupon} />
//               ))}
//             </div>
//           ) : (
//             <p className='text-center'>No coupons found.</p>
//           )}
//         </div>
//       )}

//       {/* Pagination controls (same as TextMessageCouponPage) */}
//       {/* ... */}
//     </div>
//   );
// };

// const CouponCard = React.memo(({ coupon }) => (
//   <div className='bg-white shadow-lg p-4 rounded relative'>
//     <h2 className='text-lg font-semibold'>Coupon Code: {coupon.code}</h2>
//     <p className='text-sm text-gray-500'>
//       Message Type:{' '}
//       {coupon.textMessage
//         ? 'Text Message'
//         : coupon.welcomeTextMessage
//         ? 'Welcome Message'
//         : 'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Message Content:{' '}
//       {coupon.textMessage?.content ||
//         coupon.welcomeTextMessage?.content ||
//         'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Subscriber Phone: {coupon.customer?.phoneNumber || 'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Status: {coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Created: {new Date(coupon.createdAt).toLocaleString()}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Redeemed Date:{' '}
//       {coupon.redeemedAt ? new Date(coupon.redeemedAt).toLocaleString() : 'N/A'}
//     </p>
//     <p className='text-sm text-gray-500'>
//       Expires:{' '}
//       {coupon.expirationDate
//         ? new Date(coupon.expirationDate).toLocaleString()
//         : 'N/A'}
//     </p>
//     <div className='mt-4 flex flex-wrap gap-2'>
//       <a
//         href={`https://localhost:3000/redeem/${coupon.code}`}
//         target='_blank'
//         rel='noopener noreferrer'
//         className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
//       >
//         <FaEye className='inline-block mr-2' />
//         View Redeem Page
//       </a>
//     </div>
//   </div>
// ));

// export default AllCouponsList;
